// src/services/stripe.js
import { loadStripe } from '@stripe/stripe-js';
import { PLATFORM } from '../constants';

// Production safety check: Prevent test keys in production builds
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';

if (isProduction && stripeKey && stripeKey.startsWith('pk_test_')) {
  throw new Error('Production environment cannot use test Stripe keys. Check your .env.production file.');
}

if (!stripeKey || stripeKey === 'pk_test_...') {
  // Invalid Stripe key configuration
}

// Initialize Stripe with your publishable key
// npm run dev uses .env.local (test keys)
// npm run prod uses .env.production (live keys)
const stripePromise = loadStripe(
  stripeKey || 'pk_test_...',
  {
    // Suppress HTTPS warning in development
    ...(process.env.NODE_ENV === 'development' && {
      stripeAccount: undefined,
      locale: 'auto'
    })
  }
);

export default stripePromise;

// Stripe Connect Standard configuration for Redbird Markets
export const STRIPE_CONFIG = {
  currency: 'usd',
  country: 'US',
  // Fee structure: Sellers pay platform fee (Stripe handles all liability)
  platformFeePercentage: PLATFORM.FEE_PERCENTAGE, // Your 5% platform fee
  // Stripe's processing fees (2.9% + $0.30) are handled by Standard Connect
  
  // Connect Standard URLs  
  returnUrl: `${process.env.REACT_APP_APP_URL || 'http://localhost:3000'}/seller-success`,
  refreshUrl: `${process.env.REACT_APP_APP_URL || 'http://localhost:3000'}/seller-retry`,
  
  // Business profile for Connect accounts
  businessProfile: {
    name: 'Redbird Markets Seller',
    url: process.env.REACT_APP_APP_URL || 'http://localhost:3000',
    support_url: `${process.env.REACT_APP_APP_URL || 'http://localhost:3000'}/support`,
  }
};

// Calculate fees for seller-commission marketplace (UPDATED: Seller pays commission)
export const calculateMarketplaceFees = (amount) => {
  const itemPrice = parseFloat(amount);
  const platformFee = itemPrice * STRIPE_CONFIG.platformFeePercentage;
  const sellerReceives = itemPrice - platformFee;
  
  // NEW STRUCTURE: Buyer pays exact listing price, seller pays TWO fees:
  // 1. Platform fee (5%) - Your marketplace commission
  // 2. Stripe processing fee (2.9% + $0.30) - Stripe's card processing fee
  // Stripe automatically deducts both fees via application_fee_amount + their standard processing
  // Seller receives: itemPrice - platformFee - Stripe's processing fee (~2.9% + $0.30)
  
  return {
    itemPrice: Math.round(itemPrice * 100) / 100,
    platformFee: Math.round(platformFee * 100) / 100,
    buyerPays: Math.round(itemPrice * 100) / 100, // Buyer pays EXACT listing price (no extra fees!)
    sellerReceives: Math.round(sellerReceives * 100) / 100, // Seller gets listing price minus 5% platform fee (Stripe fee also deducted)
    platformRevenue: Math.round(platformFee * 100) / 100, // Your 5% commission from seller
    commissionRate: STRIPE_CONFIG.platformFeePercentage * 100, // 5% platform fee (plus Stripe's 2.9% + $0.30)
  };
};

// Updated fee calculation for seller-commission model
export const calculateFees = (amount) => {
  const fees = calculateMarketplaceFees(amount);
  return {
    subtotal: fees.itemPrice,
    serviceFee: 0, // NO buyer fees anymore!
    stripeFee: 0, // Hidden in Connect Standard
    total: fees.buyerPays, // Buyer pays exact listing price
    // New seller-focused fields
    sellerCommission: fees.platformFee,
    sellerPayout: fees.sellerReceives,
    commissionRate: fees.commissionRate,
  };
};

// Convert dollars to cents for Stripe
export const dollarsTocents = (dollars) => Math.round(parseFloat(dollars) * 100);

// Convert cents to dollars for display
export const centsToDollars = (cents) => (cents / 100).toFixed(2);

// Payment status constants
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  CANCELED: 'canceled',
  REFUNDED: 'refunded',
};

// Create payment intent on your backend
export const createPaymentIntent = async (amount, listingId, sellerId, buyerId) => {
  try {
    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: dollarsTocents(amount),
        currency: STRIPE_CONFIG.currency,
        listingId,
        sellerId,
        buyerId,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create payment intent');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Process refund
export const processRefund = async (paymentIntentId, amount = null) => {
  try {
    const response = await fetch('/api/process-refund', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        paymentIntentId,
        amount: amount ? dollarsTocents(amount) : null,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to process refund');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};